import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

// Components
import Service from 'components/atoms/Service';
import SelectAllButton from 'components/atoms/SelectAllButton';

// Constants
import { SERVICES_TYPE, SERVICES_KIND } from 'constants/services';

// Utils
import { toCapitalize } from 'utils/string';
import { findServiceById, findServiceByProperty } from 'utils/callbacks';

import './DeclinedServicesList.scss';

const ns = 'services-list';

class DeclinedServicesList extends React.Component {
  constructor(props) {
    super(props);
    this.clickHandler = this.clickHandler.bind(this);
    this.state = {
      allSelected: false
    };
  }

  componentDidMount() {
    this.checkAllServices();
  }

  selectAllServices(ev) {
    ev.preventDefault();
    const { allSelected } = this.state;
    const {
      serviceList,
      apptDeclinedServices,
      apptServices,
      gtmUserEvent
    } = this.props;
    if (!allSelected) {
      serviceList.forEach(service => {
        const isSelected =
          apptDeclinedServices.find(
            findServiceByProperty('operationItemId', service.operationItemId)
          ) || apptServices.find(findServiceById(service.id));
        if (!isSelected) {
          this.addService(service, true);
        }
      });
      gtmUserEvent(
        'All Declined / Deferred Services',
        'Selected',
        'Declined / Deferred Services Added To Appointment',
        `GTM_CURRENT_PAGE > Declined / Deferred Services Section`,
        `All declined Services`
      );
    } else {
      serviceList.forEach(service => {
        const isSelected =
          apptDeclinedServices.find(
            findServiceByProperty('operationItemId', service.operationItemId)
          ) || apptServices.find(findServiceById(service.id));
        if (isSelected) {
          this.removeService(service);
        }
      });
    }
    this.setState({ allSelected: !allSelected });
  }

  addService(service, addingAllServices) {
    const {
      deferredServices,
      declinedServices,
      addService,
      addDeclinedService,
      gtmUserEvent
    } = this.props;
    let serviceType = '';
    if (
      declinedServices.find(
        findServiceByProperty('operationItemId', service.operationItemId)
      )
    ) {
      addDeclinedService(service);
      serviceType = toCapitalize(SERVICES_KIND.DECLINED);
    }
    if (deferredServices.find(findServiceById(service.id))) {
      addService(service);
      serviceType = toCapitalize(SERVICES_KIND.DEFERRED);
    }
    addingAllServices && this.checkAllServices(service.id);
    !addingAllServices &&
      gtmUserEvent(
        `${serviceType} Service`,
        `Selected`,
        `${serviceType} Service Added To Appointment`,
        `GTM_CURRENT_PAGE > Declined / Deferred Services Section`,
        toCapitalize(service.name)
      );
  }

  removeService(service) {
    const {
      declinedServices,
      deferredServices,
      removeService,
      removeDeclinedService
    } = this.props;
    if (
      declinedServices.find(
        findServiceByProperty('operationItemId', service.operationItemId)
      )
    ) {
      removeDeclinedService(service.operationItemId);
    }
    if (deferredServices.find(findServiceById(service.id))) {
      removeService(service.id);
    }
    this.setState({ allSelected: false });
  }

  checkAllServices(serviceId) {
    const {
      serviceList,
      selectedDeclinedServicesIds,
      selectedDeferredServicesIds
    } = this.props;
    const servicesAvailable = serviceList.map(service => service.id);
    const servicesSelected = selectedDeclinedServicesIds.concat(
      selectedDeferredServicesIds
    );
    if (serviceId) {
      servicesSelected.push(serviceId);
    }
    if (
      servicesSelected.sort().toString() ===
        servicesAvailable.sort().toString() &&
      servicesSelected.length + servicesAvailable.length > 0
    ) {
      this.setState({ allSelected: true });
    }
  }

  clickHandler(service) {
    const {
      apptDeclinedServices,
      apptServices,
      updateReadOnlyServices
    } = this.props;
    const { quoteServiceId } = service;
    const isSelected =
      apptDeclinedServices.find(
        findServiceByProperty('operationItemId', service.operationItemId)
      ) || apptServices.find(findServiceById(service.id));
    if (!isSelected) {
      this.addService(service);
    } else {
      this.removeService(service);
    }

    if (quoteServiceId) {
      updateReadOnlyServices(service);
    }
  }

  render() {
    const { allSelected } = this.state;
    const {
      serviceList,
      className,
      apptServices,
      apptDeclinedServices
    } = this.props;
    const totalPrice = serviceList.reduce((a, b) => a + b.price, 0);
    const discountedTotalPrice = serviceList.reduce(
      (a, b) => a + b.discountedPrice || 0,
      0
    );
    const classProps = classNames(ns, className);
    return (
      <div className={classProps} role={'list'}>
        <SelectAllButton
          isSelected={allSelected}
          price={totalPrice}
          discountedPrice={discountedTotalPrice}
          onClick={ev => this.selectAllServices(ev)}
        />
        {serviceList.map(service => {
          const { id, promotion, operationItemId } = service;
          const isSelected = Boolean(
            apptDeclinedServices.find(
              findServiceByProperty('operationItemId', operationItemId)
            ) || apptServices.find(findServiceById(id))
          );
          const techComments = service.notes
            ? `Technician Comments: ${service.notes}`
            : null;
          return (
            <Service
              key={operationItemId || id} // Declined service use operationItemId as unique identifier
              service={service}
              onSelect={this.clickHandler}
              isSelected={isSelected}
              type={SERVICES_TYPE.ALACARTE}
              techComments={techComments}
              promotion={promotion}
            />
          );
        })}
      </div>
    );
  }
}

DeclinedServicesList.propTypes = {
  addDeclinedService: PropTypes.func.isRequired,
  addService: PropTypes.func.isRequired,
  apptDeclinedServices: PropTypes.array,
  apptServices: PropTypes.array,
  className: PropTypes.string,
  currentPath: PropTypes.string.isRequired,
  declinedServices: PropTypes.array,
  deferredServices: PropTypes.array,
  group: PropTypes.string,
  gtmUserEvent: PropTypes.func.isRequired,
  removeDeclinedService: PropTypes.func.isRequired,
  removeService: PropTypes.func.isRequired,
  selectedDeclinedServicesIds: PropTypes.array,
  selectedDeferredServicesIds: PropTypes.array,
  serviceList: PropTypes.array.isRequired,
  updateReadOnlyServices: PropTypes.array.isRequired
};

export default DeclinedServicesList;



// WEBPACK FOOTER //
// ./src/components/molecules/DeclinedServicesList/DeclinedServicesList.js